import { useEffect, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchMyKeys, fetchMyTokens } from "../lib/api";
import type { MyBearerToken, MyKey } from "../lib/types";
import ConsentPanel from "../components/ConsentPanel";
import DataTable from "../components/DataTable";
import WriteOpsHint from "../components/WriteOpsHint";
import { ME_WRITE_OPS } from "../writeops/me";

const KEY_COLS: ColumnDef<MyKey, any>[] = [
  {
    header: "Fingerprint",
    accessorKey: "fingerprint",
    cell: (c) => (
      <span className="text-xs font-mono text-kw-text-muted">
        {c.getValue() as string}
      </span>
    ),
  },
  { header: "Name", accessorKey: "display_name", cell: (c) => c.getValue() ?? "—" },
  { header: "Status", accessorKey: "status" },
  {
    header: "Expires",
    accessorKey: "expires_at",
    cell: (c) => (c.getValue() as string)?.slice(0, 10) ?? "—",
  },
];

const TOKEN_COLS: ColumnDef<MyBearerToken, any>[] = [
  {
    header: "Token ID",
    accessorKey: "token_id",
    cell: (c) => (
      <span className="text-xs font-mono text-kw-text-muted">
        {c.getValue() as string}
      </span>
    ),
  },
  { header: "Realm Pin", accessorKey: "realm_pin", cell: (c) => c.getValue() ?? "—" },
  {
    header: "Scopes",
    accessorKey: "scopes",
    cell: (c) => (
      <span className="text-xs font-mono text-kw-text-muted">
        {((c.getValue() as string[]) ?? []).join(" ") || "—"}
      </span>
    ),
  },
  {
    header: "Status",
    accessorKey: "revoked_at",
    cell: (c) => ((c.getValue() as string | null) ? "revoked" : "active"),
  },
];

export default function MeRoute() {
  const [keys, setKeys] = useState<MyKey[]>([]);
  const [tokens, setTokens] = useState<MyBearerToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [k, t] = await Promise.all([fetchMyKeys(), fetchMyTokens()]);
        setKeys(k);
        setTokens(t);
      } catch (e: any) {
        setError(e.message ?? String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="px-8 py-6 space-y-8">
      <div>
        <h2 className="text-lg font-medium">Me</h2>
        <p className="text-kw-text-muted text-sm mt-1">
          Self-service view of your own consents, SSH keys and bearer tokens
          (scoped to your authenticated subject).
        </p>
      </div>
      <section id="consents" className="space-y-3">
        <h3 className="text-sm font-medium text-kw-text-muted uppercase tracking-wide">
          Pack Consents
        </h3>
        <ConsentPanel />
      </section>
      {loading && <div className="text-kw-text-muted text-sm">Lade...</div>}
      {error && <div className="text-red-400 text-sm">{error}</div>}
      {!loading && !error && (
        <>
          <section className="space-y-3">
            <h3 className="text-sm font-medium text-kw-text-muted uppercase tracking-wide">
              SSH Keys (I2)
            </h3>
            <DataTable
              columns={KEY_COLS}
              data={keys}
              emptyHint="Keine registrierten SSH-Keys."
            />
          </section>
          <section className="space-y-3">
            <h3 className="text-sm font-medium text-kw-text-muted uppercase tracking-wide">
              Bearer Tokens (I3)
            </h3>
            <DataTable
              columns={TOKEN_COLS}
              data={tokens}
              emptyHint="Keine Bearer-Tokens für dein Subject."
            />
          </section>
        </>
      )}
      <WriteOpsHint title="Me-Write-Ops" ops={ME_WRITE_OPS} />
    </div>
  );
}
